/**
 * JSDoc 解析器
 *
 * 从 Demo 组件源代码中提取 JSDoc 注释并解析元数据
 * 存放位置: features/mpc-common/src/devtools/demo-router
 * @since 6666-AI组件复用基建 T1.3: 新增 aiTags / complexity / useCase / sourceComponent / reusable / lastVerified 解析
 */

import { DemoMeta, VALID_CATEGORIES, Complexity } from './types';

// ── aiTags 受控词表（与 .ai-doc/index/ai-tags-dictionary.md 同步）──
const AI_TAGS_VOCABULARY: ReadonlySet<string> = new Set([
  // 数据形态
  'form', 'table', 'list', 'tree', 'card', 'chart', 'timeline',
  // 交互范式
  'select', 'search', 'filter', 'sort', 'paginate', 'upload', 'import', 'export', 'drag', 'batch', 'copy',
  // UI 模式
  'modal', 'drawer', 'panel', 'tabs', 'popover', 'tooltip', 'breadcrumb', 'stepper',
  // 业务域
  'supplier', 'bidding', 'contract', 'demand', 'invoice', 'finance', 'settlement', 'auth', 'serviceConfig',
  // 特殊能力
  'multi', 'single', 'editable', 'readonly', 'nested', 'lazy', 'collapsible', 'fullscreen', 'responsive',
  // 元数据/辅助（demo 专用）
  'demo', 'showcase', 'test', 'hot-reload',
]);

/**
 * 从源代码中提取 JSDoc 注释
 * @param sourceCode 组件源代码
 * @returns JSDoc 注释字符串，如果没有则返回 null
 */
export function extractJSDoc(sourceCode: string): string | null {
  // 匹配 /** ... */ 格式的注释
  const jsdocRegex = /\/\*\*([\s\S]*?)\*\//;
  const match = sourceCode.match(jsdocRegex);
  
  if (!match) {
    return null;
  }
  
  return match[1].trim();
}

/**
 * 解析 JSDoc 标签
 * @param jsdocContent JSDoc 注释内容
 * @returns 标签键值对
 */
export function parseJSDocTags(jsdocContent: string): Record<string, string> {
  const tags: Record<string, string> = {};
  
  // 按行分割并处理每一行
  const lines = jsdocContent.split('\n');
  
  for (const line of lines) {
    // 移除行首的 * 和空白字符
    const cleanedLine = line.replace(/^\s*\*\s?/, '').trim();
    
    // 匹配 @tag value 格式
    const tagMatch = cleanedLine.match(/^@(\w+)\s*(.*)$/);
    if (tagMatch) {
      const [, tagName, tagValue] = tagMatch;
      tags[tagName] = tagValue.trim();
    }
  }
  
  return tags;
}

/**
 * 从文件路径生成默认标题
 * 例如：supplier-card -> Supplier Card
 * @param filePath 文件路径
 * @returns 默认标题
 */
function generateDefaultTitleFromPath(filePath: string): string {
  // 提取 $demo/ 后的目录名
  const match = filePath.match(/\$demo[/\\]([^/\\]+)/);
  if (!match) {
    return 'Untitled Demo';
  }
  
  return match[1]
    .replace(/-/g, ' ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * 解析逗号分隔的分类字符串为数组
 * 支持英文逗号和中文逗号，自动 trim、去重、去空值
 */
function parseCategoryString(value: string): string[] {
  const result = value
    .split(/[,，]/)
    .map((s) => s.trim())
    .filter(Boolean);
  // 去重
  return [...new Set(result)];
}

/**
 * 校验分类值，对不在固定集合内的分类输出警告
 */
function validateCategories(categories: string[], filePath: string): void {
  for (const cat of categories) {
    if (!(VALID_CATEGORIES as readonly string[]).includes(cat)) {
      // console.warn(
      //   `[demo-router] 未知分类 "${cat}" (文件: ${filePath})，有效分类: ${VALID_CATEGORIES.join(', ')}`
      // );
    }
  }
}

/**
 * 从文件路径生成分类（多分类数组）
 * @param filePath 文件路径
 * @returns 分类数组
 */
function generateCategoryFromPath(filePath: string): string[] {
  if (filePath.includes('containers/')) {
    return ['业务'];
  }
  if (filePath.includes('components/SForm/')) {
    return ['核心', '表单'];
  }
  if (/Table|TableForm|ProTable/.test(filePath)) {
    return ['表格'];
  }
  if (filePath.includes('components/')) {
    return ['其他'];
  }
  return ['其他'];
}

/**
 * 从文件路径生成路由路径
 * @param filePath 文件绝对路径
 * @returns 路由路径
 */
function generateRoutePath(filePath: string): string {
  // 提取 $demo/ 后的相对路径
  const demoIndex = filePath.indexOf('$demo/');
  if (demoIndex === -1) {
    return '';
  }
  
  let relativePath = filePath.slice(demoIndex + 6); // 跳过 '$demo/'
  
  // 移除 index.tsx 或 index.ts
  // 处理边缘情况：$demo/index.tsx（没有子目录时，回退到 $demo 的父目录名）
  if (relativePath === 'index.tsx' || relativePath === 'index.ts') {
    const beforeDemo = filePath.slice(0, demoIndex);
    const parts = beforeDemo.split('/').filter(Boolean);
    relativePath = parts[parts.length - 1] || 'unnamed-demo';
  } else {
    relativePath = relativePath.replace(/\/index\.(tsx|ts)$/, '');
  }
  
  // 转换为小写 kebab-case
  relativePath = relativePath
    .replace(/([A-Z])/g, '-$1')
    .toLowerCase()
    .replace(/^-/, '');
  
  return `/demos/${relativePath}`;
}

/**
 * 解析 aiTags 数组（逗号分隔，trim + 去重 + 去空）
 */
export function parseTagsString(value: string): string[] {
  const result = value
    .split(/[,，]/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  return [...new Set(result)];
}

/**
 * 校验 aiTags，对不在受控词表中的 tag 输出 warning（不阻塞）
 */
export function validateAiTags(tags: string[], filePath: string): void {
  for (const tag of tags) {
    if (!AI_TAGS_VOCABULARY.has(tag)) {
      // console.warn(
      //   `[demo-router] aiTag "${tag}" 不在受控词表中 (文件: ${filePath})，`
      //   + `建议同步 .ai-doc/index/ai-tags-dictionary.md 或使用已有标签`
      // );
    }
  }
}

/**
 * 解析 useCase 字符串为数组（分号或逗号分隔）
 */
export function parseUseCaseString(value: string): string[] {
  return value
    .split(/[;；,，]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * 校验 complexity 值
 */
export function validateComplexity(value: string): Complexity | undefined {
  const valid: Complexity[] = ['simple', 'medium', 'complex'];
  return valid.includes(value as Complexity) ? (value as Complexity) : undefined;
}

/**
 * 解析 Demo 元数据
 * @param sourceCode 组件源代码
 * @param filePath 文件路径（用于生成默认值）
 * @returns DemoMeta 对象
 */
export function parseDemoMeta(sourceCode: string, filePath: string): DemoMeta {
  // 提取 JSDoc 注释
  const jsdocContent = extractJSDoc(sourceCode);
  
  // 解析标签
  const tags = jsdocContent ? parseJSDocTags(jsdocContent) : {};
  
  // 生成路由路径
  const routePath = generateRoutePath(filePath);
  
  // ── 解析分类（@category — DEPRECATED，向后兼容）──
  const category = tags.category
    ? parseCategoryString(tags.category)
    : generateCategoryFromPath(filePath);
  validateCategories(category, filePath);

  // ── 解析 aiTags（T1.3 新增，主键。兼容 @aiTags 和 @aitags 大小写）──
  const tagsAiTags = tags.aitags || tags.aiTags;
  const aiTags = tagsAiTags
    ? parseTagsString(tagsAiTags)
    : undefined;
  if (aiTags && aiTags.length > 0) {
    validateAiTags(aiTags, filePath);
  }

  // ── 解析 complexity（T1.3 新增）──
  const complexity = tags.complexity
    ? validateComplexity(tags.complexity)
    : undefined;

  // ── 解析 useCase（T1.3 新增。兼容 @useCase 和 @usecase 大小写）──
  const tagsUseCase = tags.usecase || tags.useCase;
  const useCase = tagsUseCase
    ? parseUseCaseString(tagsUseCase)
    : undefined;

  // ── 解析 sourceComponent（T1.3 新增。兼容 @sourceComponent 和 @sourcecomponent 大小写）──
  const sourceComponent = tags.sourcecomponent || tags.sourceComponent || undefined;

  // ── 解析 reusable（T1.3 新增）──
  const reusable = tags.reusable
    ? tags.reusable.toLowerCase() === 'true'
    : undefined;

  // ── 解析 lastVerified（T1.3 新增。兼容 @lastVerified 和 @lastverified 大小写）──
  const lastVerified = tags.lastverified || tags.lastVerified || undefined;

  // ── 解析 routeParams（动态路由参数，如 ":tabkey"）──
  const routeParams = tags.routeparams || tags.routeParams || undefined;

  // 构建 DemoMeta 对象
  const meta: DemoMeta = {
    title: tags.title || generateDefaultTitleFromPath(filePath),
    description: tags.description || '',
    category,
    author: tags.author || '',
    date: tags.date || new Date().toISOString().split('T')[0],
    path: routePath,
    componentPath: filePath,
    filePath,
    // 新增字段
    aiTags,
    complexity,
    useCase,
    sourceComponent,
    reusable,
    lastVerified,
    routeParams,
  };
  
  return meta;
}

/**
 * 验证 DemoMeta 是否有效
 * @param meta DemoMeta 对象
 * @returns 验证结果和错误信息
 */
export function validateDemoMeta(meta: DemoMeta): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!meta.title || meta.title.trim() === '') {
    errors.push('缺少必填字段: @title');
  }
  
  if (!meta.path || meta.path.trim() === '') {
    errors.push('无法生成路由路径');
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}
